import NotificationDeleteConfirmModal from "./notifications/NotificationDeleteConfirmModal";
import NotificationRuleModal from "./notifications/NotificationRuleModal";
import NotificationRulesTable from "./notifications/NotificationRulesTable";
import {
  Box,
  Grid,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import Component from "components/Component";
import RawConfigEditor from "components/admin/RawConfigEditor";
import Button from "components/common/Button";
import * as config from "config";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as adminService from "services/adminService";
import * as staticDataService from "services/staticDataService";
import * as utils from "utils";

const NotificationTriggers = () => {
  const { companyId } = useParams();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [overallEnabled, setOverallEnabled] = useState(false);
  const [notificationRules, setNotificationRules] = useState([]);
  const [userLogMessage, setUserLogMessage] = useState("");
  const [saveErrorMsg, setSaveErrorMsg] = useState("");

  // Track initial state for reset functionality
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [initialRules, setInitialRules] = useState([]);

  // Fetch notifications configuration from company config
  const {
    data: notificationsConfig,
    isLoading,
    refetch,
  } = staticDataService.useConfigForCompanyQuery(
    { name: "notifications", companyId },
    { skip: !companyId }
  );

  // Update configuration mutation - use admin service proxy like Navigation does
  const [
    updateConfig,
    { isLoading: isSaving, isError: saveError },
  ] = adminService.useAdminUpdateNotificationConfigMutation();

  // Load configuration when it's fetched
  useEffect(() => {
    if (notificationsConfig) {
      const enabled = notificationsConfig.enabled ?? false;
      const rules = notificationsConfig.triggers ?? [];
      setOverallEnabled(enabled);
      setNotificationRules(rules);
      setInitialEnabled(enabled);
      setInitialRules(rules);
    }
  }, [notificationsConfig]);

  const pollForChanges = useCallback(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.staticData("retrieve-config"),
      {
        headers: { "Content-Type": "application/json" },
        method: "post",
        body: JSON.stringify({ keys: ["notifications"] }),
      },
      companyId
    );
    const json = await response.json();
    return json.notifications;
  }, [companyId]);

  const handleConfirmDelete = () => {
    if (deleteTarget) {
      const updatedRules = notificationRules.filter(
        (rule) => rule.id !== deleteTarget.id
      );
      setNotificationRules(updatedRules);
    }
    setDeleteTarget(null);
  };

  const handleSaveRule = (rule) => {
    let updatedRules;
    if (editTarget) {
      // Edit existing rule
      updatedRules = notificationRules.map((r) =>
        r.id === editTarget.id
          ? { ...rule, id: editTarget.id, enabled: r.enabled ?? true }
          : r
      );
      setEditTarget(null);
    } else {
      // Create new rule
      const newRule = { ...rule, id: Date.now().toString(), enabled: true };
      updatedRules = [...notificationRules, newRule];
      setIsCreateOpen(false);
    }
    setNotificationRules(updatedRules);
  };

  const handleToggleRule = (ruleId) => {
    const updatedRules = notificationRules.map((r) =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    setNotificationRules(updatedRules);
  };

  const handleToggleOverallEnabled = (enabled) => {
    setOverallEnabled(enabled);
  };

  const handleSave = async () => {
    try {
      setSaveErrorMsg("");
      const result = await updateConfig({
        companyId,
        config: {
          enabled: overallEnabled,
          triggers: notificationRules,
        },
        userLogMessage,
      }).unwrap();
      if (
        result &&
        (result.statusCode === "403" || result.statusCode === "401")
      ) {
        setSaveErrorMsg(
          "You do not have permission to update notification configuration. Please contact an administrator."
        );
        return;
      }
      await refetch();
      setUserLogMessage("");
    } catch (error) {
      setSaveErrorMsg(
        "Failed to save notification configuration. Please try again."
      );
    }
  };

  const handleReset = () => {
    setOverallEnabled(initialEnabled);
    setNotificationRules(initialRules);
    setUserLogMessage("");
  };

  if (isLoading) {
    return (
      <Component title={"Notification Triggers"}>
        <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      </Component>
    );
  }

  return (
    <Component
      title={"Notification Triggers"}
      options={
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <FormControlLabel
            control={
              <Switch
                checked={overallEnabled}
                onChange={(e) => handleToggleOverallEnabled(e.target.checked)}
                disabled={isSaving}
              />
            }
            label={"Enabled"}
          />
          <Button
            color={"primary"}
            onClick={() => setIsCreateOpen(true)}
            disabled={isSaving}
          >
            {"+ Add"}
          </Button>
        </Stack>
      }
    >
      <RawConfigEditor
        data={notificationsConfig}
        onSave={async (parsedJson, logMessage) => {
          await updateConfig({
            companyId,
            config: parsedJson,
            userLogMessage: logMessage,
          }).unwrap();
          await refetch();
        }}
        isSaving={isSaving}
        pollForChanges={pollForChanges}
      >
        <Grid item xs={12}>
          <Box sx={{ pb: 2 }}>
            <NotificationRulesTable
              rules={notificationRules}
              onRequestEdit={(rule) => setEditTarget(rule)}
              onRequestDelete={(rule) => setDeleteTarget(rule)}
              onToggleEnabled={handleToggleRule}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ pt: 2 }}>
            <TextField
              variant={"filled"}
              label={"Reason for change"}
              fullWidth
              onChange={(e) => setUserLogMessage(e.target.value)}
              value={userLogMessage}
            />
            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              spacing={2}
              sx={{ mt: 2, mb: 2 }}
            >
              <Button
                color={"primary"}
                onClick={handleSave}
                disabled={isSaving}
              >
                {"Save"}
              </Button>
              <Button
                color={"secondary"}
                onClick={handleReset}
                disabled={isSaving}
              >
                {"Reset"}
              </Button>
            </Stack>

            {(saveError || !!saveErrorMsg) && (
              <Alert severity={"error"}>
                {saveErrorMsg ||
                  "Failed to save notification configuration. Please try again."}
              </Alert>
            )}
            {isSaving && <Alert severity={"info"}>{"Saving..."}</Alert>}
          </Box>
        </Grid>
      </RawConfigEditor>
      <NotificationRuleModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleSaveRule}
        mode={"create"}
      />
      <NotificationRuleModal
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveRule}
        mode={"edit"}
        rule={editTarget}
      />
      <NotificationDeleteConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </Component>
  );
};

export default NotificationTriggers;
